import { type ActionReducerMapBuilder, isAnyOf } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import type { OrderById, SubmitOrderResponse, ValidateOrderResponse } from '@/store/orders/state.ts';
import { takeProfitOrderApi } from '@/store/orders/takeProfitOrder/api/index.ts';
import type { ValidateTakeProfitRequest } from '@/store/orders/takeProfitOrder/api/validateTakeProfit.ts';
import { isTakeProfitOrder } from '@/store/orders/takeProfitOrder/helpers.ts';
import type { TakeProfitOrder, TakeProfitOrderProperties } from '@/store/orders/takeProfitOrder/state.ts';

const mapProperties = (
  currentOrder: TakeProfitOrder,
  originalArgs: ValidateTakeProfitRequest,
  payload?: ValidateOrderResponse,
): TakeProfitOrderProperties => {
  const currentTime = Date.now();
  const clockOffset =
    payload?.backendTime !== null && payload?.backendTime !== 0 && typeof payload?.backendTime === 'number'
      ? payload?.backendTime - currentTime
      : undefined;

  return {
    status: currentOrder.properties.status,
    isReadyToSubmit: Boolean(payload?.isReadyToSubmit),
    mode: currentOrder.properties.mode,
    clockOffset,

    currencyPair: {
      currency1: originalArgs.currency1,
      currency2: originalArgs.currency2,
      tiering: currentOrder.properties.currencyPair?.tiering,
    },

    clientWay: originalArgs.way ?? currentOrder.properties.clientWay,

    notional: originalArgs.notional ?? currentOrder.properties.notional,
    notionalCurrencyIndex: originalArgs.notionalCurrencyIndex ?? currentOrder.properties.notionalCurrencyIndex,

    limitPrice: payload?.limitPrice ?? undefined,
    customerPrice: payload?.customerPrice ?? undefined,
    commercialMargin: payload?.margin ?? undefined,

    expiry: originalArgs.expiry,

    notificationEmails: originalArgs.notificationEmails.isOverridenEmails
      ? originalArgs.notificationEmails.emails
      : currentOrder.properties.notificationEmails,
  };
};

export const validateTakeProfitFulfilledExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(takeProfitOrderApi.endpoints.validateTakeProfit.matchFulfilled, (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;

    const currentOrder = state[tileId];

    if (isNotDefined(currentOrder) || currentOrder.type !== 'TakeProfitOrder') {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    state[tileId] = {
      type: 'TakeProfitOrder',
      clientId: currentOrder.clientId,

      properties: {
        ...mapProperties(currentOrder, action.meta.arg.originalArgs, action.payload),

        errors: {},
        warnings: {},
      },
    };
  });

export const validateTakeProfitPendingExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(isAnyOf(takeProfitOrderApi.endpoints.validateTakeProfit.matchPending), (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;

    const currentOrder = state[tileId];

    if (isNotDefined(currentOrder) || !isTakeProfitOrder(currentOrder)) {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    state[tileId] = {
      ...currentOrder,

      properties: {
        ...currentOrder.properties,

        clientWay: action.meta.arg.originalArgs.way,
        notional: action.meta.arg.originalArgs.notional,
        notionalCurrencyIndex: action.meta.arg.originalArgs.notionalCurrencyIndex,

        limitPrice: action.meta.arg.originalArgs.limitPrice ?? undefined,
        customerPrice: action.meta.arg.originalArgs.customerPrice ?? undefined,
        commercialMargin: action.meta.arg.originalArgs.margin ?? undefined,

        expiry:
          'expiry' in action.meta.arg.originalArgs
            ? action.meta.arg.originalArgs.expiry
            : currentOrder.properties.expiry,
        notificationEmails:
          action.meta.arg.originalArgs.notificationEmails.emails ?? currentOrder.properties.notificationEmails,
      },
    };
  });

export const submitTakeProfitRejectedExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(
      takeProfitOrderApi.endpoints.submitTakeProfit.matchRejected,
      takeProfitOrderApi.endpoints.updateTakeProfit.matchRejected,
    ),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const actionType = action.meta.arg.endpointName;

      const currentOrder = state[tileId];
      const payload = isPostSubmitOrderResponse(action.payload) ? action.payload : undefined;

      if (
        isNotDefined(currentOrder) ||
        isNotDefined(payload) ||
        currentOrder.type !== 'TakeProfitOrder' ||
        payload.type !== 'failure'
      ) {
        return state;
      }

      const errorMessage = (() => {
        if (payload.errors.some((error) => error.errorDescription === 'timeout')) {
          return 'order.timeout';
        }

        if (payload.errors.some((error) => error.errorDescription === 'server.error')) {
          return 'order.serverError';
        }

        if (payload.errors.some((error) => error.errorDescription === 'order.unexpected')) {
          return 'order.unexpected';
        }

        return 'order.validationFailed';
      })();

      const areErrorsKnown = payload.errors.some((error) => error.field !== 'unknown');

      const errors = payload.errors.reduce(
        (acc, error) => {
          if (error.field === 'amountInCcy1' || error.field === 'amountInCcy2') {
            acc.notional = error;
          }

          if (error.field === 'margin') {
            acc.commercialMargin = error;
          }

          if (error.field === 'base64Emails') {
            acc.notificationEmails = error;
          }

          acc[error.field] = error;
          return acc;
        },
        {} as Record<string, { errorCode: number; errorDescription: string; field: string }>,
      );

      state[tileId] = {
        ...currentOrder,
        type: 'TakeProfitOrder',
        properties: {
          ...currentOrder.properties,
          status: areErrorsKnown
            ? { type: actionType === 'submitTakeProfit' ? 'creating' : 'submitted' }
            : { type: 'failed', errorMessage },
          errors: areErrorsKnown ? errors : {},
        },
      } as TakeProfitOrder;
    },
  );

export const validateTakeProfitRejectedExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(takeProfitOrderApi.endpoints.validateTakeProfit.matchRejected, (state, action) => {
    const tileId = action.meta.arg.originalArgs.tileId;

    const currentOrder = state[tileId];
    const payload = isValidateOrderResponse(action.payload) ? action.payload : undefined;

    if (isNotDefined(currentOrder) || currentOrder.type !== 'TakeProfitOrder') {
      return state;
    }

    if (
      currentOrder.properties.status.type === 'pending' ||
      (currentOrder.properties.status.type === 'submitted' && currentOrder.properties.mode !== 'EDIT')
    ) {
      return state;
    }

    if (isNotDefined(payload)) {
      state[tileId] = {
        ...currentOrder,
        type: 'TakeProfitOrder',
        properties: {
          ...currentOrder.properties,
          status: { type: 'failed', errorMessage: 'order.unexpected' },
        },
      } as TakeProfitOrder;
      return;
    }

    const errors = isDefined(payload?.validationResponseDetailContent)
      ? payload.validationResponseDetailContent.reduce(
          (acc, error) => {
            if (error.field === 'amountInCcy1' || error.field === 'amountInCcy2') {
              acc.notional = error;
            }

            if (error.field === 'base64Emails') {
              acc.notificationEmails = error;
            }

            if (error.field === 'margin') {
              acc.commercialMargin = error;
            }

            acc[error.field] = error;
            return acc;
          },
          {} as Record<string, { errorCode: number; errorDescription: string; field: string }>,
        )
      : undefined;

    state[tileId] = {
      ...currentOrder,
      type: 'TakeProfitOrder',
      properties: {
        ...currentOrder.properties,
        ...mapProperties(currentOrder, action.meta.arg.originalArgs, payload),

        errors,
      },
    } as TakeProfitOrder;
  });

export const submitTakeProfitFulfilledExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(
      takeProfitOrderApi.endpoints.submitTakeProfit.matchFulfilled,
      takeProfitOrderApi.endpoints.updateTakeProfit.matchFulfilled,
    ),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const currentOrder = state[tileId];

      if (isNotDefined(currentOrder) || !isTakeProfitOrder(currentOrder) || action.payload.type === 'failure') {
        return state;
      }

      state[tileId] = {
        ...currentOrder,

        properties: {
          ...currentOrder.properties,

          status: {
            type: 'submitted',
            tradeId: action.payload.orderId,
          },

          isReadyToSubmit: false,
        },
      };
    },
  );

export const submitTakeProfitPendingExtraReducer = (builder: ActionReducerMapBuilder<OrderById>) =>
  builder.addMatcher(
    isAnyOf(
      takeProfitOrderApi.endpoints.submitTakeProfit.matchPending,
      takeProfitOrderApi.endpoints.updateTakeProfit.matchPending,
    ),
    (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const currentOrder = state[tileId];

      if (isNotDefined(currentOrder) || !isTakeProfitOrder(currentOrder)) {
        return state;
      }

      state[tileId] = {
        ...currentOrder,

        properties: {
          ...currentOrder.properties,
          status: {
            type: 'pending',
          },
        },
      };
    },
  );

function isValidateOrderResponse(response: any): response is ValidateOrderResponse {
  return response && 'validationResponseDetailContent' in response;
}

function isPostSubmitOrderResponse(response: any): response is SubmitOrderResponse {
  return response && 'errors' in response;
}
